import React, {
	FC,
	PropsWithChildren,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';

import { isEqual } from 'lodash';

import {
	FrequencyDto,
	IScheduledJobConfiguration
} from '../../../interfaces/ScheduledJobConfiguration';

import { SchedulingSectionContext } from './schedulingSectionContext';
import { SchedulingSectionContextTypeProps } from './SchedulingSectionContext.types';

export const SchedulingSectionContextWrapper: FC<
	PropsWithChildren<SchedulingSectionContextTypeProps>
> = (props) => {
	const { children, initialConfig, onConfigChange, error, isListMode = false } = props;

	const [config, setConfig] = useState<IScheduledJobConfiguration>(initialConfig);
	const previousInitialConfigRef = useRef(initialConfig);

	useEffect(() => {
		if (isEqual(previousInitialConfigRef.current, initialConfig)) {
			return;
		}

		previousInitialConfigRef.current = initialConfig;
		setConfig(initialConfig);
	}, [initialConfig]);

	const updateConfig = useCallback(
		(updates: Partial<IScheduledJobConfiguration>) => {
			setConfig((prevConfig) => {
				let newConfig = { ...prevConfig, ...updates };

				if (updates.frequency !== undefined && updates.frequency !== prevConfig.frequency) {
					if (updates.frequency === FrequencyDto.MONTHLY) {
						newConfig = { ...newConfig, wdays: [] };
					}
					if (
						updates.frequency === FrequencyDto.WEEKLY ||
						updates.frequency === FrequencyDto.EVERY_OTHER_WEEK
					) {
						newConfig = {
							...newConfig,
							mdays: [],
							lastDayOfMonth: false,
							startMonth: undefined,
							startYear: undefined,
							months: []
						};
					}
				}

				onConfigChange(newConfig);
				return newConfig;
			});
		},
		[onConfigChange]
	);

	const value = useMemo(
		() => ({
			config,
			updateConfig,
			error,
			isListMode
		}),
		[config, updateConfig, error, isListMode]
	);

	return (
		<SchedulingSectionContext.Provider value={value}>{children}</SchedulingSectionContext.Provider>
	);
};
